import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Package,
  ShoppingBag,
  TrendingUp,
  AlertTriangle,
  Clock,
  Star,
  Mail,
} from 'lucide-react';
import { adminAPI } from '../../api/admin';
import { DashboardStats } from '../../types';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  index: number;
}> = ({ title, value, icon, color = 'bg-white', index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    className={`${color} p-6 border border-light`}
  >
    <div className="flex items-center justify-between mb-4">
      <p className="text-xs tracking-widest uppercase text-nude">{title}</p>
      <div className="text-nude">{icon}</div>
    </div>
    <p className="font-serif text-3xl text-primary">{value}</p>
  </motion.div>
);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminAPI.getDashboardStats();
        if (res.success && res.data) setStats(res.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-28 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    pending: 'text-amber-600 bg-amber-50',
    processing: 'text-blue-600 bg-blue-50',
    shipped: 'text-purple-600 bg-purple-50',
    delivered: 'text-green-600 bg-green-50',
    cancelled: 'text-red-500 bg-red-50',
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">
          Overview
        </p>
        <h1 className="font-serif text-3xl text-primary">Dashboard</h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={`$${Number(stats?.total_revenue || 0).toFixed(2)}`}
          icon={<TrendingUp size={18} />}
          index={0}
        />
        <StatCard
          title="Total Orders"
          value={stats?.total_orders || 0}
          icon={<ShoppingBag size={18} />}
          index={1}
        />
        <StatCard
          title="Customers"
          value={stats?.total_users || 0}
          icon={<Users size={18} />}
          index={2}
        />
        <StatCard
          title="Products"
          value={stats?.total_products || 0}
          icon={<Package size={18} />}
          index={3}
        />
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="bg-amber-50 border border-amber-200 p-4"
        >
          <div className="flex items-center space-x-2 mb-2">
            <Clock size={14} className="text-amber-600" />
            <p className="text-xs tracking-widest uppercase text-amber-600">
              Pending
            </p>
          </div>
          <p className="font-serif text-2xl text-amber-700">
            {stats?.pending_orders || 0}
          </p>
          <p className="text-xs text-amber-600 mt-1">Orders pending</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="bg-red-50 border border-red-200 p-4"
        >
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle size={14} className="text-red-500" />
            <p className="text-xs tracking-widest uppercase text-red-500">
              Low Stock
            </p>
          </div>
          <p className="font-serif text-2xl text-red-600">
            {stats?.low_stock_variants || 0}
          </p>
          <p className="text-xs text-red-500 mt-1">Variants low</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="bg-blue-50 border border-blue-200 p-4"
        >
          <div className="flex items-center space-x-2 mb-2">
            <Star size={14} className="text-blue-600" />
            <p className="text-xs tracking-widest uppercase text-blue-600">
              Reviews
            </p>
          </div>
          <p className="font-serif text-2xl text-blue-700">
            {stats?.pending_reviews || 0}
          </p>
          <p className="text-xs text-blue-600 mt-1">Pending approval</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className="bg-green-50 border border-green-200 p-4"
        >
          <div className="flex items-center space-x-2 mb-2">
            <Mail size={14} className="text-green-600" />
            <p className="text-xs tracking-widest uppercase text-green-600">
              Subscribers
            </p>
          </div>
          <p className="font-serif text-2xl text-green-700">
            {stats?.newsletter_subscribers || 0}
          </p>
          <p className="text-xs text-green-600 mt-1">Newsletter</p>
        </motion.div>
      </div>

      {/* Recent Orders */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.8 }}
        className="bg-white border border-light"
      >
        <div className="px-6 py-4 border-b border-light">
          <h2 className="font-serif text-lg text-primary">Recent Orders</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light">
                {['Order', 'Status', 'Payment', 'Total', 'Date'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats?.recent_orders?.map((order) => (
                <tr
                  key={order.id}
                  className="border-b border-light hover:bg-light/50 transition-colors"
                >
                  <td className="px-6 py-4 text-xs tracking-wide font-medium">
                    {order.order_number}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      statusColors[order.status] || 'text-primary bg-light'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase ${
                      order.payment_status === 'paid'
                        ? 'text-green-600'
                        : 'text-amber-600'
                    }`}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs">
                    ${Number(order.total).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Monthly Revenue */}
      {stats?.monthly_revenue && stats.monthly_revenue.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9 }}
          className="bg-white border border-light p-6"
        >
          <h2 className="font-serif text-lg text-primary mb-6">
            Revenue Last 6 Months
          </h2>
          <div className="flex items-end space-x-4 h-32">
            {stats.monthly_revenue.map((month, index) => {
              const maxRevenue = Math.max(
                ...stats.monthly_revenue.map((m) => Number(m.revenue))
              );
              const height =
                maxRevenue > 0
                  ? (Number(month.revenue) / maxRevenue) * 100
                  : 0;
              return (
                <div
                  key={index}
                  className="flex-1 flex flex-col items-center space-y-2"
                >
                  <p className="text-xs text-nude">
                    ${Number(month.revenue).toFixed(0)}
                  </p>
                  <div
                    className="w-full bg-primary transition-all duration-500"
                    style={{ height: `${height}%` }}
                  />
                  <p className="text-xs text-nude">
                    {new Date(month.month).toLocaleDateString('en-US', {
                      month: 'short',
                    })}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DashboardPage;